import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import { PERMISSION_MODES, PermissionMode } from "@cohesive/api-client";
import { Check, Hand, ShieldCheck, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { PermissionModeControl } from "../hooks/usePermissionModeControl";

interface PermissionModeOption {
  value: PermissionMode;
  label: string;
  description: string;
  icon: typeof Hand;
}

const OPTIONS: PermissionModeOption[] = [
  {
    value: PERMISSION_MODES.CONFIRM_MUTATIONS,
    label: "Manual",
    description: "Every mutating command asks for confirmation",
    icon: Hand,
  },
  {
    value: PERMISSION_MODES.AUTO,
    label: "Auto",
    description: "The agent decides; risky commands still ask",
    icon: ShieldCheck,
  },
  {
    value: PERMISSION_MODES.ALLOW_ALL,
    label: "Allow all",
    description: "Auto-accept all commands this session",
    icon: TriangleAlert,
  },
];

interface ConfirmationModeButtonProps {
  control: PermissionModeControl;
  iconClassName?: string;
  triggerButtonClassName?: string;
}

/**
 * Permission mode ("permissions") for the session this composer is attached
 * to. The mode is server state on the flow session — `permission_mode` — but
 * the switch rides the next message rather than a write of its own, so the
 * control drives a {@link PermissionModeControl} and the composer stamps the
 * pick onto the send.
 *
 * The control sits in the toolbar's icon cluster from first paint, so the mode
 * is visible on opening a chat rather than only once a gated command has
 * surfaced a confirmation. With `control.mode` undefined — no session object
 * in hand, and no draft to drive — it renders in the same fixed box, disabled
 * and dimmed, so nothing shifts when the session arrives.
 *
 * The icon split mirrors the reasoning-effort selector next door: the trigger
 * carries the current value, and the fly-up menu — same width, radius, row
 * height and selected-row treatment — carries the choice. `lc-warning` is the
 * design system's caution token, the same one SessionBypassNotice uses for the
 * live allow-all banner.
 */
export function ConfirmationModeButton({
  control,
  iconClassName,
  triggerButtonClassName,
}: ConfirmationModeButtonProps) {
  const [open, setOpen] = useState(false);

  const active = OPTIONS.find((option) => option.value === control.mode);
  const isUnknown = !active;
  const allowAll = active?.value === PERMISSION_MODES.ALLOW_ALL;
  const modeLabel = active?.label;
  const TriggerIcon = active?.icon ?? ShieldCheck;

  return (
    <div className="z-9 order-2">
      <Popover open={open} onOpenChange={setOpen}>
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                disabled={isUnknown}
                aria-label={
                  isUnknown
                    ? "Permission mode"
                    : `Permission mode: ${modeLabel}`
                }
                className={cn(
                  "transition-colors",
                  allowAll
                    ? "text-lc-warning hover:text-lc-warning hover:bg-lc-warning/15"
                    : "text-muted-foreground hover:text-foreground",
                  isUnknown && "opacity-40",
                  triggerButtonClassName,
                )}
              >
                <TriggerIcon className={iconClassName} />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            {isUnknown ? "Permission mode" : `Permission mode: ${modeLabel}`}
          </TooltipContent>
        </Tooltip>

        <PopoverContent
          side="top"
          align="start"
          sideOffset={8}
          className="w-72 p-2 rounded-[14px] border"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          {OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => {
                setOpen(false);
                control.setMode(option.value);
              }}
              className={cn(
                "w-full flex items-center gap-3 min-h-11 px-4 py-2 rounded-lg text-left transition-colors",
                "hover:bg-accent",
                control.mode === option.value && "bg-accent/60",
              )}
            >
              <option.icon
                className={cn(
                  "size-4 shrink-0",
                  option.value === PERMISSION_MODES.ALLOW_ALL &&
                    "text-lc-warning",
                )}
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm">{option.label}</div>
                <div className="text-xs text-muted-foreground leading-snug">
                  {option.description}
                </div>
              </div>
              {control.mode === option.value && (
                <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
              )}
            </button>
          ))}
        </PopoverContent>
      </Popover>
    </div>
  );
}
